<template>
  <div class="relative">
    <div class="flex mt-6 mb-10">
      <div class="w-full md:w-2/3 md:mx-auto md:max-w-md px-4">
        <h1 class="my-4 text-3xl">
          Register
        </h1>
        <form
          @submit.prevent="register"
          @keydown="form.onKeydown($event)"
        >
          <!-- Name -->
          <text-input
            name="name"
            :form="form"
            label="Name"
            :required="true"
          />

          <!-- Email -->
          <text-input
            name="email"
            :form="form"
            label="Email"
            :required="true"
          />

          <select-input
            name="hear_about_us"
            :options="hearAboutUsOptions"
            :form="form"
            label="How did you hear about us?"
            :required="true"
          />

          <!-- Password -->
          <text-input
            native-type="password"
            name="password"
            :form="form"
            label="Password"
            :required="true"
          />

          <!-- Password Confirmation -->
          <text-input
            class="mb-4"
            native-type="password"
            name="password_confirmation"
            :form="form"
            label="Confirm Password"
            :required="true"
          />

          <!-- Submit Button -->
          <v-button
            class="w-full mt-4"
            :loading="form.busy"
          >
            Create account
          </v-button>

          <p class="text-gray-600/50 text-sm text-center my-4">
            Or
          </p>
          <v-button
            native-type="buttom"
            color="white"
            class="space-x-4 w-full"
            :loading="false"
            @click.prevent="signInwithGoogle"
          >
            <Icon name="devicon:google" />
            <span class="mx-2">Sign in with Google</span>
          </v-button>

          <p class="text-gray-500 mt-4 text-sm text-center">
            Already have an account?
            <NuxtLink
              :to="{ name: 'login' }"
              class="font-semibold ml-1"
            >
              Log In
            </NuxtLink>
          </p>
        </form>
      </div>
    </div>
    <airtable-forms-footer class="border-t z-50 relative" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useNuxtApp } from "nuxt/app";

const { $utm } = useNuxtApp();
definePageMeta({
  middleware: 'guest',
  stickyNavbar: true
})
defineRouteRules({
  swr: 3600
})
useAirtableFormsSeoMeta({
  title: 'Register'
})

const crisp = useCrisp()
const router = useRouter()
const logEvent = useAmplitude().logEvent
const authStore = useAuthStore()
const providersStore = useOAuthProvidersStore()

const form = useForm({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  utm_data: null
})

const hearAboutUsOptions = computed(() => {
  const options = [
    { name: 'Facebook', value: 'facebook' },
    { name: 'Twitter', value: 'twitter' },
    { name: 'Reddit', value: 'reddit' },
    { name: 'Search Engine (Google, DuckDuckGo...)', value: 'search_engine' },
    { name: 'Friend or Colleague', value: 'friend_colleague' },
    { name: 'Blog/Article', value: 'blog_article' }
  ]
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)
  options.push({ name: 'Other', value: 'other' })
  return options
})

async function register () {
  // Register the user.
  try {
    form.utm_data = $utm.value
    await form.post('register')
  } catch (error) {
    if (error && error.data && error.data.message)
      useAlert().error(error.data.message)
    else
      useAlert().error('Something went wrong, please try again.')

    return
  }

  // Log in the user.
  const tokenData = await form.post('login')

  // Save the token.
  authStore.setToken(tokenData.token)

  // Update the user.
  const userData = await airtableFormsFetch('user')
  authStore.setUser(userData)

  // Track event
  logEvent('register', { source: form.hear_about_us })
  useGtm().trackEvent({
    event: 'register',
    source: form.hear_about_us
  })

  // Disable dark mode when registering
  if (import.meta.client)
    document.body.classList.remove('dark')

  crisp.pushEvent('register')

  // Redirect home.
  router.push({ name: 'home' })
}

function signInwithGoogle() {
  providersStore.guestConnect('google', true)
}
</script>
